import { isArray, isEqual } from 'lodash';

import { select } from '../../../index';
import type { Model, RootState, SelectorCreator, Slicer } from '../../types';
import type { PSNPartnerNotification } from '../../types/Notification';

export enum Size {
  EXTRA_EXTRA_LARGE = 'xxl',
  EXTRA_LARGE = 'xl',
  EXTRA_SMALL = 'xs',
  FLEX = 'flex',
  LARGE = 'lg',
  MEDIUM = 'md',
  SMALL = 'sm'
}

export interface Modal {
  path: string;
  locationState?: object;
}

export type ModalState = Modal[];

const isPSNPartnerNotificationmodal = ({ path, locationState }: Modal) =>
  path === '/notifications/psn-partner' && locationState && 'templateId' in locationState;

const reducers = {
  enqueue: (state: ModalState, payload: Modal[] | Modal): ModalState => [
    ...state,
    ...(isArray(payload) ? payload : [payload])
  ],
  unshift: (state: ModalState, payload: Modal[] | Modal): ModalState =>
    isArray(payload) ? [...(payload as Modal[]), ...state] : [payload as Modal, ...state],
  unique: (state: ModalState, payload: Modal): ModalState =>
    !state.find(
      ({ path, locationState }) =>
        path === payload.path &&
        (isPSNPartnerNotificationmodal(payload)
          ? isEqual(locationState, payload.locationState)
          : true)
    )
      ? [...state, payload]
      : state,
  unshiftUnique: (state: ModalState, payload: Modal): ModalState =>
    !state.find(({ path }) => path === payload.path) ? [payload, ...state] : state,
  replace: (state: ModalState, payload: Modal[]) => [...payload, ...state.slice(1)],
  close: (state: ModalState, payload?: Modal['path']): ModalState =>
    state.length && (!payload || state[0].path.startsWith(payload)) ? state.slice(1) : state,
  unqueuePSNPartnerNotification: (
    state: ModalState,
    payload: { path: Modal['path']; notification: PSNPartnerNotification }
  ): ModalState =>
    state.reduce<ModalState>((acc, modal) => {
      if (modal.path === payload.path && isEqual(modal.locationState, payload.notification)) {
        return acc;
      }
      acc.push(modal);
      return acc;
    }, []),
  clear: (): ModalState => []
};

const selectors = (slice: Slicer<ModalState>, createSelector: SelectorCreator) => ({
  getCurrent() {
    return slice<Modal | undefined>((state) => state[0]);
  },
  isModalOpen() {
    return createSelector(
      (rootState: RootState) => select.modal.getCurrent(rootState),
      (currentModal) => !!currentModal?.path
    );
  }
});

const modal: Model<ModalState, typeof reducers, void, void, typeof selectors> = {
  state: [],
  reducers,
  selectors
};

export default modal;
